import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import type { ComponentProps } from 'react'
import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import _PriceRowPrice from '../_PriceRowPrice'
import PriceRowValueDiscount from '../Value/PriceRowValueDiscount'
import Typography from 'LuxKit/Typography/Typography'
import clsx from 'clsx'

type InheritedBasePriceRowPriceProps = Pick<ComponentProps<typeof _PriceRowPrice>, 'className' | 'discountPercentage' | 'discountType' | 'saleUnit' | 'signDisplay' | 'size' | 'priceFormat' | 'caption'>

interface Props extends InheritedBasePriceRowPriceProps, Utils.PropsWithDataAttributes {
  /**
   * Main price
   */
  memberPrice: number
  /**
   * Prepend the price with the LuxPlus logo
   */
  logoPlacement?: 'leading' | 'trailing'
}

const SIZES_HEADING_VARIANTS: Record<Props['size'], ComponentProps<typeof Heading>['variant']> = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
}

function PriceRowLuxPlusPrice(props: Props) {
  const {
    memberPrice,
    logoPlacement,
    saleUnit,
    size,
    className,
    caption,
    priceFormat = 'roundedDollar',
    signDisplay,
    discountPercentage,
    discountType,
    ...rest
  } = props

  const saleUnitLabel = useMemo(() => {
    if (!saleUnit) return undefined
    const sanitisedSaleUnit = saleUnit.toLocaleLowerCase().replace(/^\//, '')
    if (/^(total|with)/.test(sanitisedSaleUnit)) return sanitisedSaleUnit
    return `/${sanitisedSaleUnit}`
  }, [saleUnit])

  if (logoPlacement === 'trailing') {
    return <Typography className={clsx(className, PriceRowLuxPlusPrice.name)} wrap="word-wrap" {...rest}>
      <FormattedMessage
        id="ibF+tLTzlZ"
        defaultMessage="{captionBlock}<price>{formattedPrice}</price>{saleUnitBlock} with {luxPlusLogo}"
        description="LuxPlus member price with trailing logo, e.g. '$99 /night with LuxPlus+'"
        values={{
          captionBlock: caption
            ? <><Caption as="span" variant="medium" colour="neutral-two" weight="normal" format="propercase">{caption}</Caption>{' '}</>
            : '',
          formattedPrice: <FormatCurrency value={memberPrice} format={priceFormat} signDisplay={signDisplay} />,
          price: (chunks) => <Heading as="span" variant={SIZES_HEADING_VARIANTS[size]} colour="lux-plus" wrap="no-wrap">{chunks}</Heading>,
          saleUnitBlock: saleUnitLabel
            ? <>{' '}<Caption as="span" variant="medium" colour="lux-plus" weight="normal" format="lowercase">{saleUnitLabel}</Caption></>
            : '',
          luxPlusLogo: <LuxPlusLogoWrapper height={8} />,
        }}
      />
      {!!discountPercentage && <>
        {' '}
        <PriceRowValueDiscount
          discountPercentage={discountPercentage}
          type={discountType}
        />
      </>}
    </Typography>
  }

  return <_PriceRowPrice
    {...rest}
    className={clsx(className, PriceRowLuxPlusPrice.name)}
    size={size}
    price={memberPrice}
    priceColour="lux-plus"
    saleUnitColour="lux-plus"
    priceLeading={logoPlacement === 'leading' ? <><LuxPlusLogoWrapper height={8} />&nbsp;</> : undefined}
    saleUnit={saleUnit}
    caption={caption}
    priceFormat={priceFormat}
    signDisplay={signDisplay}
    discountPercentage={discountPercentage}
    discountType={discountType}
  />
}

export default PriceRowLuxPlusPrice
